import React, { useState, useEffect, useMemo, useRef } from "react";
import moment from "moment";
import { req } from "../../utils";
import { Grid } from "@mui/material/";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import fileContractIcon from "../../assets/images/file-contract-regular.svg";
import { differenceInCalendarDays } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
const purplePalette = [
  "#B3A6FF",  // Lavender
  "#9C6BFF",  // Amethyst
  "#6F32C8"   // Royal Purple
];

const bluePalette = [
  "#6EC6FF",  // Sky Blue
  "#5AA2D4",  // Ocean Blue
  "#274A78"   // Midnight Blue
];

const orangePalette = [
  "#FFC19E",  // Soft Peach
  "#FF8A3D",  // Amber
  "#D45A2A"   // Burnt Orange
];

function useDeepCompareEffect(effect, deps) {
  const prevDeps = useRef();
  const isFirst = useRef(true);
  if (
    isFirst.current ||
    !prevDeps.current ||
    deps.length !== prevDeps.current.length ||
    !deps.every((dep, i) => {
      if (Array.isArray(dep) && Array.isArray(prevDeps.current[i])) {
        return dep.length === prevDeps.current[i].length && dep.every((v, j) => v === prevDeps.current[i][j]);
      }
      return dep === prevDeps.current[i];
    })
  ) {
    isFirst.current = false;
    prevDeps.current = deps;
    effect();
  }
}

export const PipedriveLeads = ({ filterProps }) => {
  const { dateRanges, pageFilterByType } = filterProps;
  const emptyLeadStats = {
    mostRecentLead: null,
    totalLeadsPerBDM: [],
    totalLeadsInTimeRange: 0,
    leadCreationRate: {},
  };
  const [leadsStats, setLeadsStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Determine grouping granularity
  const days = dateRanges && dateRanges.length === 2 ? differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0])) : 0;
  const groupByMonth = days > 31;
  const getGroupKey = (dateStr) => {
    const date = new Date(dateStr);
    return groupByMonth
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      : date.toISOString().split('T')[0];
  };

  const generateLeadStats = useMemo(() => {
    return (leadsData, startDate = null, endDate = null) => {
      const safeLeadsData = Array.isArray(leadsData) ? leadsData : [];
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : new Date();

      const filteredLeads = safeLeadsData.map((bdm) => {
        const safeLeadItems = Array.isArray(bdm?.Leads) ? bdm.Leads : [];
        if (bdm) {
          console.log("PipedriveLeads - bdm object from leadsData:", bdm);
        }
        return {
          BDM: bdm?.BDM || "Unknown",
          Leads: safeLeadItems.filter((lead) => {
            const leadAddDate = new Date(lead.add_time);
            return (!start || leadAddDate >= start) && leadAddDate <= end;
          }),
        };
      });

      // Total Leads Per BDM
      const totalLeadsPerBDM = filteredLeads.map((bdm) => ({
        name: bdm.BDM,
        number: bdm.Leads.length,
      }));

      //search through all leads where update_time is in the date range and count them
      const totalLeadsInTimeRange = filteredLeads.reduce(
        (total, bdm) =>
          total +
          bdm.Leads.filter((lead) => {
            const leadUpdateDate = new Date(lead.add_time);
            return leadUpdateDate >= start && leadUpdateDate <= end;
          }).length,
        0
      );

      // Lead Creation Rate (grouped by day or month)
      const leadCreationRate = () => {
        const creationRates = {};
        filteredLeads.forEach((bdm) => {
          bdm.Leads.forEach((lead) => {
            const key = getGroupKey(lead.add_time);
            if (!creationRates[key]) {
              creationRates[key] = {};
            }
            creationRates[key][bdm.BDM] = (creationRates[key][bdm.BDM] || 0) + 1;
          });
        });
        // Add BDMs with zero leads to each date
        Object.keys(creationRates).forEach((date) => {
          filteredLeads.forEach((bdm) => {
            if (!creationRates[date][bdm.BDM]) {
              creationRates[date][bdm.BDM] = 0;
            }
          });
        });
        return creationRates;
      };

      // Finding Most Recent Lead
      const mostRecentLead = filteredLeads.reduce((mostRecent, bdm) => {
        const recentLead = bdm.Leads.reduce((latest, lead) => {
          const leadAddDate = new Date(lead.add_time);
          return leadAddDate > new Date(latest.add_time) ? lead : latest;
        }, bdm.Leads[0]);
        return recentLead &&
          (!mostRecent ||
            new Date(recentLead.add_time) > new Date(mostRecent.add_time))
          ? recentLead
          : mostRecent;
      }, null);

      // Return the stats in the requested format
      return {
        mostRecentLead: mostRecentLead,
        totalLeadsPerBDM: totalLeadsPerBDM,
        totalLeadsInTimeRange: totalLeadsInTimeRange,
        leadCreationRate: leadCreationRate(),
      };
    };
  }, [groupByMonth]);

  useDeepCompareEffect(() => {
    const fetchLeadsData = async () => {
      setIsLoading(true);
      try {
        const startDate = dateRanges
          ? moment(dateRanges[0]).unix()
          : moment().subtract(30, "days").unix();
        const endDate = dateRanges
          ? moment(dateRanges[1]).unix()
          : moment().unix();
        const url = `get_leads?s=${startDate}&e=${endDate}`;
        const data = await req("GET", url);
        
        if (data && data.length > 0) {
          const startDateParam = moment.unix(startDate).format("YYYY-MM-DD");
          const endDateParam = moment.unix(endDate).format("YYYY-MM-DD");
          const newLeadsStats = generateLeadStats(data, startDateParam, endDateParam);
          setLeadsStats({ ...emptyLeadStats, ...newLeadsStats });
        } else {
          setLeadsStats(emptyLeadStats);
        }
      } catch (error) {
        console.error("Error fetching leads data:", error);
        setLeadsStats(prev => prev || emptyLeadStats);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLeadsData();
  }, [dateRanges, generateLeadStats]);

  const chartData = useMemo(() => {
    if (!leadsStats) return null;
    const safeLeadCreationRate = leadsStats.leadCreationRate || {};
    const safeTotalLeadsPerBDM = Array.isArray(leadsStats.totalLeadsPerBDM)
      ? leadsStats.totalLeadsPerBDM
      : [];
    const sortedDates = Object.keys(safeLeadCreationRate).sort();
    const bdms = [...new Set(safeTotalLeadsPerBDM.map((bdm) => bdm.name))];

    const bdmColorMap = {
      "kirk":purplePalette[0],  // Lavender
      "Dennis": bluePalette[0],  // Ocean Blue
      "Matt Miliano": orangePalette[0],  // Amber
      "Brad": purplePalette[1], // Amethyst
      "Frank": bluePalette[1] // Ocean Blue
    };

    const datasets = bdms.map((bdm) => {
      const data = sortedDates.map((date) => safeLeadCreationRate?.[date]?.[bdm] || 0);
      const hasLeads = data.some(value => value > 0);
      
      return {
        label: bdm,
        data: data,
        backgroundColor: bdmColorMap[bdm] || "#B3A6FF", // Default to Lavender if not specified
        hidden: !hasLeads, // Hide the dataset if there are no leads
      };
    }).filter(dataset => !dataset.hidden); // Remove hidden datasets from the final array

    return {
      labels: sortedDates,
      datasets: datasets,
    };
  }, [leadsStats]);

  const chartOptions = {
    responsive: true,
    scales: {
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: (() => {
            if (dateRanges && dateRanges.length === 2) {
              const days = differenceInCalendarDays(new Date(dateRanges[1]), new Date(dateRanges[0]));
              return days > 31 ? 'month' : 'day';
            }
            return 'day';
          })()
        }
      },
      y: {
        stacked: true,
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      title: {
        display: true,
        text: "Lead Creation Rate by BDM",
      },
    },
  };

  const formatDate = (date) => moment(date).format("DD/MM/YYYY");

  if (isLoading && !leadsStats) return (
    <Grid
      item
      xs={12}
      sm={12}
      md={6}
      lg={6}

      className={`${
        pageFilterByType !== "unfiltered" && pageFilterByType !== "contract"
          ? "d-none"
          : ""
      }`}
    >
      <div className="card card-stat-summary animate-bg-primary">
        <div className="card-body">
          <div className="card-stat-summary--title">PipeDrive Leads</div>
          <div className="card-stat-summary--total-wrapper">
            <div>Loading...</div>
          </div>
        </div>
        <div className="card-bg">
          <img
            src={fileContractIcon}
            alt="File Contract Icon"
            className="bg-icon"
          />
        </div>
      </div>
    </Grid>
  );

  if (!leadsStats) return null;

  return (
    <Grid
      item
      xs={12}
      sm={12}
      md={6}
      lg={6}
      className={`${
        pageFilterByType !== "unfiltered" && pageFilterByType !== "contract"
          ? "d-none"
          : ""
      }`}
    >
      <div className="card card-stat-summary">
        <div className="card-body">
          <div className="card-stat-summary--title">PipeDrive Leads</div>
          <div className="card-stat-summary--total-wrapper"></div>
          <div className="card-stat-summary--total-wrapper">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6}>
                <div className="card-stat-summary--total">
                  {leadsStats.totalLeadsInTimeRange}
                  <div className="card-stat-summary--total-subtitle">
                    Total leads
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>

          {chartData && (
            <div className="lead-creation-chart">
              <Bar options={chartOptions} data={chartData} />
            </div>
          )}
          
          {leadsStats.mostRecentLead && leadsStats.mostRecentLead.title && (
            <div className="most-recent-lead" style={{ marginTop: '1rem' }}>
              <h4>Most Recent Lead</h4>
              <p><strong>Title:</strong> {leadsStats.mostRecentLead.title}</p>
              <p><strong>Added:</strong> {formatDate(leadsStats.mostRecentLead.add_time)}</p>
              <p><strong>BDM:</strong> {leadsStats.mostRecentLead.owner}</p>
            </div>
          )}
      
          <div className="card-stat-summary--meta">
            <div className="card-stat-summary--meta-timeline">
              <span className="text-persiangreen">Date range:</span>{" "}
              {formatDate(dateRanges[0])} - {formatDate(dateRanges[1])}
            </div>
          </div>
        </div>
        <div className="card-bg">
          <img
            src={fileContractIcon}
            alt="File Contract Icon"
            className="bg-icon"
          />
        </div>
      </div>
    </Grid>
  );
};
